"use client"

import { cn } from "@/lib/utils"
import { motion } from "framer-motion"
import React from "react"

export interface MarqueeImage {
    src: string
    alt: string
    href?: string
    target?: "_blank" | "_self" | "_parent" | "_top"
}

export interface ThreeDMarqueeProps {
    images: MarqueeImage[]
    className?: string
    cols?: number // default is 4
    onImageClick?: (image: MarqueeImage, index: number) => void
}

export const ThreeDMarquee: React.FC<ThreeDMarqueeProps> = ({
    images,
    className = "",
    cols = 4,
    onImageClick,
}) => {
    // Clone the image list twice
    const duplicatedImages = [...images, ...images]

    const groupSize = Math.ceil(duplicatedImages.length / cols)
    const imageGroups = Array.from({ length: cols }, (_, index) =>
        duplicatedImages.slice(index * groupSize, (index + 1) * groupSize),
    )

    const handleImageClick = (image: MarqueeImage, globalIndex: number) => {
        if (onImageClick) {
            onImageClick(image, globalIndex)
        } else if (image.href) {
            window.open(image.href, image.target || "_self")
        }
    }

    return (
        <section
            className={cn(
                "mx-auto block h-full overflow-hidden rounded-2xl bg-white",
                className,
            )}
        >
            <div
                className="flex h-full w-full items-center justify-center"
                style={{
                    transform: "rotateX(55deg) rotateY(0deg) rotateZ(45deg)",
                }}
            >
                <div className="w-full scale-90 overflow-hidden sm:scale-100">
                    <div className="relative grid h-full w-full origin-center transform grid-cols-2 gap-4 sm:grid-cols-4">
                        {imageGroups.map((imagesInGroup, idx) => (
                            <motion.div
                                key={`column-${idx}`}
                                animate={{ y: idx % 2 === 0 ? 100 : -100 }}
                                transition={{
                                    duration: idx % 2 === 0 ? 10 : 15,
                                    repeat: Infinity,
                                    repeatType: "reverse",
                                }}
                                className="relative flex flex-col items-center gap-6"
                            >
                                <div className="absolute top-0 left-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700" />
                                {imagesInGroup.map((image, imgIdx) => {
                                    const globalIndex = idx * groupSize + imgIdx
                                    const isClickable =
                                        image.href || onImageClick

                                    return (
                                        <div
                                            key={`img-${imgIdx}`}
                                            className="relative"
                                        >
                                            <div className="absolute top-0 left-0 h-0.5 w-full bg-gray-200 dark:bg-gray-700" />
                                            <motion.img
                                                whileHover={{ y: -10 }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: "easeInOut",
                                                }}
                                                src={image.src}
                                                alt={image.alt}
                                                width={970}
                                                height={700}
                                                className={cn(
                                                    "aspect-[970/700] w-full max-w-[400px] rounded-lg object-cover shadow-xl ring ring-gray-300/30 transition-shadow duration-300 hover:shadow-2xl dark:ring-gray-800/50",
                                                    isClickable &&
                                                        "cursor-pointer",
                                                )}
                                                onClick={() =>
                                                    handleImageClick(
                                                        image,
                                                        globalIndex,
                                                    )
                                                }
                                            />
                                        </div>
                                    )
                                })}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
